import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Logger, LoggerFactory } from '../../core/logger.service';
import {
  FlyoutDataType,
  FlyoutOpeningData,
  OnsiteCampaignEvent,
  OnsiteCampaignEventType,
  PersonDetails,
  SessionDetails,
} from '../flyout-models';
import { Subscription } from 'rxjs';
import { FlyoutService } from '../flyout.service';
import { WidgetPreviewDialogComponent } from '../../shared/widget-preview-dialog/widget-preview-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

export interface OnsiteCampaignEventExpanded extends OnsiteCampaignEvent {
  label?: string;
  campaignName?: string;
}

@Component({
  selector: 'app-flyout-campaign-information',
  templateUrl: './flyout-campaign-information.component.html',
  styleUrls: ['./flyout-campaign-information.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutCampaignInformationComponent implements OnInit, OnDestroy {
  @ViewChild('flyoutCampaignInformationComponent', { static: true })
  flyoutCampaignInformationComponentRef: ElementRef;

  private readonly logger: Logger;
  private flyoutOpeningData: FlyoutOpeningData;
  private openingDataSubscription: Subscription;
  public personDetails: PersonDetails;
  private personSubscription: Subscription;
  private sessionDetails: SessionDetails;
  private sessionSubscription: Subscription;
  private subscriptionArray: Subscription[] = [];
  public ocEvents: Array<OnsiteCampaignEventExpanded>;
  public ocEventsToShow: Array<OnsiteCampaignEventExpanded>;
  public OnsiteCampaignEventType = OnsiteCampaignEventType;
  private widgetPreviewDialogRef: MatDialogRef<WidgetPreviewDialogComponent>;
  public index = 0;
  public spanStart: number;
  public spanEnd: number;
  public sortingProperty = 'name';
  public sortingDirection = -1;
  public expandedTab: string;

  public isMobile = false;
  private screenHeight: number;

  @HostListener('window:resize')
  getScreenHeight(): void {
    this.checkScreenSize();
  }

  constructor(
    public readonly loggerFactory: LoggerFactory,
    private readonly flyoutService: FlyoutService,
    public dialog: MatDialog,
  ) {
    this.logger = loggerFactory.get('FlyoutCampaignInformationComponent');
  }

  ngOnInit() {
    this.openingDataSubscription = this.flyoutService.flyoutOpeningDataBS.subscribe(
      (flyoutOpeningData: FlyoutOpeningData) => {
        this.flyoutOpeningData = flyoutOpeningData;
        this.expandedTab = this.flyoutOpeningData?.expandedTab;
        this.processCampaignStatistics();
      },
    );
    this.sessionSubscription = this.flyoutService.sessionDetailsBS.subscribe((sessionDetails: SessionDetails) => {
      if (sessionDetails) {
        this.sessionDetails = sessionDetails;
        this.processCampaignStatistics();
      }
    });
    this.personSubscription = this.flyoutService.personDetailsBS.subscribe((personDetails: PersonDetails) => {
      this.personDetails = personDetails;
      this.processCampaignStatistics();
    });
    this.checkScreenSize();
    this.slicePageOfEvents();
  }

  private slicePageOfEvents() {
    this.spanStart = this.index + 1;
    this.spanEnd = this.index + 5;
    this.ocEventsToShow = this.ocEvents.slice(this.index, this.index + 5);
  }

  public sortByProperty(property: string) {
    if (property === this.sortingProperty) {
      this.sortingDirection = -1 * this.sortingDirection;
    }
    this.sortingProperty = property;
    this.ocEvents.sort((a, b) => {
      return a[property] > b[property] ? this.sortingDirection * 1 : this.sortingDirection * -1;
    });
    this.index = 0;
    this.slicePageOfEvents();
  }

  private checkScreenSize(): void {
    this.isMobile = window.innerWidth <= 676;
    this.screenHeight = window.innerHeight;
  }

  public openWidgetPreview(event, widgetId: number) {
    const clickPositionX = event.clientX - 100;
    const clickPositionY = event.clientY;
    let position = undefined;
    if (!this.isMobile) {
      if (this.screenHeight < clickPositionY + 400) {
        position = { left: clickPositionX + 'px' };
      } else {
        position = { left: clickPositionX + 'px', top: clickPositionY + 'px' };
      }
    }

    if (this.widgetPreviewDialogRef) {
      this.widgetPreviewDialogRef.close();
    }
    this.widgetPreviewDialogRef = this.dialog.open(WidgetPreviewDialogComponent, {
      width: '400px',
      panelClass: 'widget-preview-dialog-component-container',
      position: position,
      hasBackdrop: true,
      autoFocus: false,
      data: {
        widgetId: widgetId,
      },
    });
  }

  private processCampaignStatistics() {
    if (this.flyoutOpeningData) {
      if (
        this.flyoutOpeningData.type === FlyoutDataType.Session &&
        this.sessionDetails &&
        this.sessionDetails.calculatedDetails
      ) {
        this.ocEvents = this.sessionDetails.ocEvents;
      } else if (this.flyoutOpeningData.type === FlyoutDataType.Contact && this.personDetails) {
        this.ocEvents = this.personDetails.ocEvents;
      }
    }
  }

  public navigateCampaigns(add: boolean) {
    if (add && this.index < this.ocEvents.length - 5) {
      this.index = this.index + 5;
    } else if (!add && this.index > 0) {
      this.index = this.index - 5;
    } else {
      return;
    }
    this.spanStart = this.index + 1;
    if (this.index < this.ocEvents.length - 5) {
      this.spanEnd = this.index + 5;
    } else {
      this.spanEnd = this.ocEvents.length;
    }
    this.ocEventsToShow = this.ocEvents.slice(this.index, this.index + 5);
  }

  ngOnDestroy(): void {
    this.subscriptionArray.push(this.openingDataSubscription, this.personSubscription, this.sessionSubscription);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }
}
